import React from 'react';
import { AlertCircle, ArrowRight, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdminVideoFileField from '@/components/admin/videoCreate/AdminVideoFileField';
import {
  MATERIAL_ACCEPT,
  MATERIAL_EXTENSIONS,
  MAX_MATERIAL_BYTES,
  MAX_QUESTIONNAIRE_URL_LENGTH,
  MAX_THUMBNAIL_BYTES,
  MAX_TITLE_LENGTH,
  MAX_VIDEO_BYTES,
  THUMBNAIL_ACCEPT,
  THUMBNAIL_EXTENSIONS,
  VIDEO_ACCEPT,
  VIDEO_EXTENSIONS,
  formatBytes,
  formatDurationSeconds,
} from '@/config/adminVideoUpload';

const sectionClassName = 'rounded-2xl border border-[#e8dccd] bg-white p-5';
const sectionTitleClassName = 'mb-4 text-sm font-semibold text-[#2d1810]';

export default function AdminVideoCreateForm({
  form,
  errors,
  thumbnailPreviewUrl,
  categories,
  categoriesLoading,
  categoriesError,
  onRetryCategories,
  onFileChange,
  onFieldChange,
  onSubmit,
}) {
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className={sectionClassName}>
        <h2 className={sectionTitleClassName}>ファイル</h2>
        <div className="space-y-5">
          <AdminVideoFileField
            id="admin-video-file"
            label="動画ファイル"
            required
            accept={VIDEO_ACCEPT}
            file={form.videoFile}
            error={errors.videoFile}
            hint={`${VIDEO_EXTENSIONS.join(' / ')}（${formatBytes(MAX_VIDEO_BYTES)}まで）。アップロード後に掲載用のMP4へ自動変換します。`}
            onChange={(file) => onFileChange('videoFile', file)}
          >
            {form.videoFile && (
              <p className="mt-1 text-xs text-[#6b5540]">
                動画時間:{' '}
                {form.durationSeconds
                  ? formatDurationSeconds(form.durationSeconds)
                  : '読み取り中（取得できない場合は変換後に自動設定されます）'}
              </p>
            )}
          </AdminVideoFileField>

          <AdminVideoFileField
            id="admin-thumbnail-file"
            label="サムネイル画像"
            required
            accept={THUMBNAIL_ACCEPT}
            file={form.thumbnailFile}
            error={errors.thumbnailFile}
            hint={`${THUMBNAIL_EXTENSIONS.join(' / ')}（${formatBytes(MAX_THUMBNAIL_BYTES)}まで）`}
            onChange={(file) => onFileChange('thumbnailFile', file)}
          >
            {thumbnailPreviewUrl && (
              <div className="mt-2 aspect-video w-full max-w-xs overflow-hidden rounded-lg border border-[#e8dccd] bg-[#f5ede3]">
                <img src={thumbnailPreviewUrl} alt="" className="h-full w-full object-cover" />
              </div>
            )}
          </AdminVideoFileField>

          <AdminVideoFileField
            id="admin-material-file"
            label="配布資料"
            accept={MATERIAL_ACCEPT}
            file={form.materialFile}
            error={errors.materialFile}
            hint={`${MATERIAL_EXTENSIONS.slice(0, 6).join(' / ')} など（${formatBytes(MAX_MATERIAL_BYTES)}まで）。未添付ならS3へのアップロードは行いません。`}
            onChange={(file) => onFileChange('materialFile', file)}
          />
        </div>
      </div>

      <div className={sectionClassName}>
        <h2 className={sectionTitleClassName}>動画情報</h2>
        <div className="space-y-5">
          <div>
            <Label htmlFor="admin-video-title" className="mb-2 block text-sm font-medium text-[#2d1810]">
              動画タイトル
              <span className="ml-1 text-xs font-normal text-[#b4462f]">必須</span>
            </Label>
            <Input
              id="admin-video-title"
              value={form.title}
              maxLength={MAX_TITLE_LENGTH}
              onChange={(event) => onFieldChange('title', event.target.value)}
              placeholder="例: フィルムスタディー 第6回"
              className="bg-[#f5ede3]"
            />
            {errors.title && <p className="mt-1 text-xs text-[#b4462f]">{errors.title}</p>}
          </div>

          <div>
            <Label htmlFor="admin-video-category" className="mb-2 block text-sm font-medium text-[#2d1810]">
              カテゴリ
              <span className="ml-1 text-xs font-normal text-[#b4462f]">必須</span>
            </Label>
            {categoriesError ? (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#e5c4bb] bg-[#fdf3f1] px-3 py-2 text-xs text-[#b4462f]">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="min-w-0">{categoriesError}</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onRetryCategories}
                  className="ml-auto h-8 gap-1 border-[#d4c4b0] text-xs text-[#5d4529] hover:bg-[#f5ede3]"
                >
                  <RotateCw className="h-3 w-3" />
                  再読み込み
                </Button>
              </div>
            ) : (
              <Select
                value={form.categoryId}
                onValueChange={(value) => onFieldChange('categoryId', value)}
                disabled={categoriesLoading || categories.length === 0}
              >
                <SelectTrigger id="admin-video-category" className="w-full bg-[#f5ede3]">
                  <SelectValue
                    placeholder={categoriesLoading ? '読み込み中…' : 'カテゴリを選択してください'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.name}
                      {category.slug ? `（${category.slug}）` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="mt-1 text-xs text-[#8b6f47]">
              categories テーブルの一覧です。動画識別子はここで選んだカテゴリの slug から採番します。
            </p>
            {errors.categoryId && <p className="mt-1 text-xs text-[#b4462f]">{errors.categoryId}</p>}
          </div>

          <div>
            <Label
              htmlFor="admin-video-questionnaire-url"
              className="mb-2 block text-sm font-medium text-[#2d1810]"
            >
              アンケートURL
              <span className="ml-1 text-xs font-normal text-[#b4462f]">必須</span>
            </Label>
            <Input
              id="admin-video-questionnaire-url"
              type="url"
              inputMode="url"
              value={form.questionnaireUrl}
              maxLength={MAX_QUESTIONNAIRE_URL_LENGTH}
              onChange={(event) => onFieldChange('questionnaireUrl', event.target.value)}
              placeholder="https://forms.example.com/xxxx"
              className="bg-[#f5ede3]"
            />
            <p className="mt-1 text-xs text-[#8b6f47]">
              再生ページのアンケートボタンのリンク先です。http:// または https:// から入力してください。
            </p>
            {errors.questionnaireUrl && (
              <p className="mt-1 text-xs text-[#b4462f]">{errors.questionnaireUrl}</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#e8dccd] bg-white p-5">
        <h2 className={sectionTitleClassName}>自動設定される項目</h2>
        <ul className="space-y-1 text-xs text-[#6b5540]">
          <li>
            動画識別子（public_id / slug / 各ファイル名）: <code>{'{カテゴリslug}-{3桁連番}'}</code>
          </li>
          <li>表示順（sort_order）: 選択したカテゴリ内の最大値 + 1</li>
          <li>動画時間（duration_seconds）: アップロードした動画から取得</li>
          <li>
            固定値: view_count 0 / series_id 1 / storage_type s3 / is_preview 0 / is_published 1 /
            is_deleted 0
          </li>
          <li>description / lesson_no: 新規追加では登録しません（NULL）</li>
        </ul>
      </div>

      {hasErrors && (
        <div className="flex items-start gap-2 rounded-xl border border-[#e5c4bb] bg-[#fdf3f1] px-4 py-3 text-sm text-[#b4462f]">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>入力内容を確認してください。</span>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#6b5540]"
        >
          アップロード
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-right text-xs text-[#8b6f47]">
        この時点ではまだアップロードしません。次の画面で内容を確認します。
      </p>
    </form>
  );
}
